/**
 * CSS Utility Functions for GuiBuilder
 * Pure functions for extracting and updating CSS values in strings
 */

/**
 * Extract a CSS property value from a CSS string for a given selector
 * @param {string} css - The CSS string to search
 * @param {string} selector - The CSS selector (e.g., '.cfg-header')
 * @param {string} property - The CSS property (e.g., 'color', 'font-size')
 * @returns {string} The property value, or empty string if not found
 */
export function extractCssValue(css, selector, property) {
    if (!css) return '';
    const escapedSelector = selector.replace(/[-\/\^$*+?.()|[\]{}]/g, '\\$&');
    const escapedProperty = property.replace(/[-\/\^$*+?.()|[\]{}]/g, '\\$&');
    const ruleRegex = new RegExp(`${escapedSelector}\\s*\\{([^}]+)\\}`, 'i');
    const match = css.match(ruleRegex);
    if (match && match[1]) {
        const propRegex = new RegExp(`(?:^|;)\\s*${escapedProperty}\\s*:\\s*([^;]+)`, 'i');
        const propMatch = match[1].match(propRegex);
        if (propMatch && propMatch[1]) {
            return propMatch[1].trim();
        }
    }
    return '';
}

/**
 * Update or add a CSS property value for a given selector
 * @param {string} css - The CSS string to modify
 * @param {string} selector - The CSS selector
 * @param {string} property - The CSS property to update
 * @param {string} value - The new value for the property
 * @returns {string} The modified CSS string
 */
export function updateCssRule(css, selector, property, value) {
    if (!css) return `${selector} { ${property}: ${value}; }`;
    const escapedSelector = selector.replace(/[-\/\^$*+?.()|[\]{}]/g, '\\$&');
    const escapedProperty = property.replace(/[-\/\^$*+?.()|[\]{}]/g, '\\$&');
    const ruleRegex = new RegExp(`(${escapedSelector}\\s*\\{[^}]*\\})`, 'g');
    
    if (ruleRegex.test(css)) {
        ruleRegex.lastIndex = 0;
        return css.replace(ruleRegex, (match) => {
            const propRegex = new RegExp(`((?:^|;)\\s*${escapedProperty}\\s*:\\s*)([^;]+)(;?)`, 'i');
            if (propRegex.test(match)) {
                return match.replace(propRegex, `$1${value}$3`);
            } else {
                return match.replace(/\s*\}$/, (closing) => {
                    const beforeClosing = match.slice(0, match.length - closing.length).trimEnd();
                    const separator = beforeClosing.endsWith(';') ? ' ' : '; ';
                    return `${separator}${property}: ${value}; }`;
                });
            }
        });
    } else {
        return css + `\n${selector} { ${property}: ${value}; }`;
    }
}

/**
 * Update SVG stroke color in CSS background-image URLs
 * @param {string} css - The CSS string containing SVG background images
 * @param {string} newColorHex - The new color in hex format (e.g., '#2563eb')
 * @returns {string} The CSS string with updated SVG stroke colors
 */
export function updateSvgStrokeInCss(css, newColorHex) {
    if (!css) return '';
    const escapedColor = encodeURIComponent(newColorHex);
    let bgImg = extractCssValue(css, '.cfg-select-input', 'background-image');
    if (bgImg && bgImg.includes('svg')) {
        const updatedBgImg = bgImg.replace(/stroke='([^']*)'/, `stroke='${escapedColor}'`);
        return updateCssRule(css, '.cfg-select-input', 'background-image', updatedBgImg);
    }
    return css;
}

/**
 * Parse a number from a string (extracts first numeric sequence)
 * @param {string|number} val - The value to parse
 * @returns {number} The parsed number, or 0 if not found
 */
export function parseNumber(val) {
    if (!val) return 0;
    const m = val.toString().match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
}
